import { Stripe, PaymentSheetEventsEnum, ApplePayEventsEnum, GooglePayEventsEnum } from '@capacitor-community/stripe';
import { isNativePlatform, isIOS, isAndroid } from '../utils/platform';

/**
 * Stripe service wrapper that handles both web and native implementations
 */

interface PaymentIntentResponse {
  clientSecret: string;
  customerId?: string;
  ephemeralKey?: string;
}

interface PaymentResult {
  success: boolean;
  error?: string;
}

/**
 * Initialize Stripe for native platforms
 * Call this on app startup if on native platform
 */
export const initializeNativeStripe = async (): Promise<void> => {
  if (!isNativePlatform()) {
    return;
  }

  const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

  if (!publishableKey) {
    console.error('VITE_STRIPE_PUBLISHABLE_KEY not found in environment');
    return;
  }

  await Stripe.initialize({
    publishableKey,
  });

  console.log('Native Stripe initialized');
};

/**
 * Present native Stripe Payment Sheet
 * @param paymentIntentData - Response from Cloud Function with clientSecret, customerId, ephemeralKey
 */
export const presentNativePaymentSheet = async (
  paymentIntentData: PaymentIntentResponse
): Promise<PaymentResult> => {
  if (!isNativePlatform()) {
    return {
      success: false,
      error: 'Not on native platform',
    };
  }

  const { clientSecret, customerId, ephemeralKey } = paymentIntentData;

  if (!customerId || !ephemeralKey) {
    return {
      success: false,
      error: 'Missing customerId or ephemeralKey from Cloud Function',
    };
  }

  try {
    // Create payment sheet
    await Stripe.createPaymentSheet({
      paymentIntentClientSecret: clientSecret,
      customerId,
      customerEphemeralKeySecret: ephemeralKey,
      merchantDisplayName: 'Wonderstood',
    });

    // Present payment sheet
    const result = await Stripe.presentPaymentSheet();

    if (result.paymentResult === PaymentSheetEventsEnum.Completed) {
      return { success: true };
    } else if (result.paymentResult === PaymentSheetEventsEnum.Canceled) {
      return { success: false, error: 'Payment canceled' };
    } else if (result.paymentResult === PaymentSheetEventsEnum.Failed) {
      return { success: false, error: 'Payment failed' };
    }

    return { success: false, error: 'Unknown payment result' };
  } catch (error) {
    console.error('Native payment sheet error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Payment failed',
    };
  }
};

/**
 * Check if Apple Pay is available
 */
export const isApplePayAvailable = async (): Promise<boolean> => {
  if (!isIOS()) {
    return false;
  }

  try {
    await Stripe.isApplePayAvailable();
    return true;
  } catch (error) {
    console.error('Error checking Apple Pay availability:', error);
    return false;
  }
};

/**
 * Present Apple Pay payment sheet
 */
export const presentApplePay = async (
  paymentIntentData: PaymentIntentResponse,
  amount: number,
  description: string = 'Professional Consultation'
): Promise<PaymentResult> => {
  if (!isIOS()) {
    return { success: false, error: 'Apple Pay only available on iOS' };
  }

  try {
    await Stripe.createApplePay({
      paymentIntentClientSecret: paymentIntentData.clientSecret,
      merchantIdentifier: 'merchant.com.wonderstood.app', // TODO: Register with Apple
      countryCode: 'US',
      currency: 'USD',
      paymentSummaryItems: [
        {
          label: description,
          amount: amount / 100, // Convert cents to dollars
        },
      ],
    });

    const result = await Stripe.presentApplePay();

    if (result.paymentResult === ApplePayEventsEnum.Completed) {
      return { success: true };
    } else if (result.paymentResult === ApplePayEventsEnum.Canceled) {
      return { success: false, error: 'Apple Pay canceled' };
    } else if (result.paymentResult === ApplePayEventsEnum.Failed) {
      return { success: false, error: 'Apple Pay failed' };
    }

    return { success: false, error: 'Unknown payment result' };
  } catch (error) {
    console.error('Apple Pay error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Apple Pay failed',
    };
  }
};

/**
 * Check if Google Pay is available
 */
export const isGooglePayAvailable = async (): Promise<boolean> => {
  if (!isAndroid()) {
    return false;
  }

  try {
    await Stripe.isGooglePayAvailable();
    return true;
  } catch (error) {
    console.error('Error checking Google Pay availability:', error);
    return false;
  }
};

/**
 * Present Google Pay payment sheet
 */
export const presentGooglePay = async (
  paymentIntentData: PaymentIntentResponse
): Promise<PaymentResult> => {
  if (!isAndroid()) {
    return { success: false, error: 'Google Pay only available on Android' };
  }

  try {
    await Stripe.createGooglePay({
      paymentIntentClientSecret: paymentIntentData.clientSecret,
    });

    const result = await Stripe.presentGooglePay();

    if (result.paymentResult === GooglePayEventsEnum.Completed) {
      return { success: true };
    } else if (result.paymentResult === GooglePayEventsEnum.Canceled) {
      return { success: false, error: 'Google Pay canceled' };
    } else if (result.paymentResult === GooglePayEventsEnum.Failed) {
      return { success: false, error: 'Google Pay failed' };
    }

    return { success: false, error: 'Unknown payment result' };
  } catch (error) {
    console.error('Google Pay error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Google Pay failed',
    };
  }
};
